/* eslint-disable no-nested-ternary, react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  Box,
  Card,
  CardHeader,
  CardContent,
  Chip,
  MenuItem,
  TextField,
  Divider,
  Tooltip,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { ArrowLeft, Info, GitCompare } from 'lucide-react';

// components
import Page from '../components/Page';
import { BaseOptionChart } from '../components/chart';
import { fCurrency, fNumber } from '../utils/formatNumber';
import * as campaignsApi from '../api/campaigns.api';

// ----------------------------------------------------------------------

export default function CompareCampaigns() {
  const navigate = useNavigate();
  const theme = useTheme();

  const [campaigns, setCampaigns] = useState([]);
  const [charts, setCharts] = useState({});
  const [compareCampAId, setCompareCampAId] = useState('');
  const [compareCampBId, setCompareCampBId] = useState('');

  const [loading, setLoading] = useState(true);

  // Load from database
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await campaignsApi.getCampaigns();
        if (response.data && response.data.success) {
          const list = response.data.data;
          setCampaigns(list);

          const chartsMap = {};
          list.forEach((camp) => {
            const daysCount = 10;
            const mockDates = [];
            const mockInstalls = [];
            const baseDate = new Date(camp.createdAt || Date.now());
            for (let i = daysCount - 1; i >= 0; i -= 1) {
              const d = new Date(baseDate);
              d.setDate(baseDate.getDate() - i);
              mockDates.push(d.toISOString().split('T')[0]);
              mockInstalls.push(Math.floor((camp.installs / daysCount) * (0.6 + Math.random() * 0.8)));
            }
            chartsMap[camp.id] = {
              daily: {
                dates: mockDates,
                installs: mockInstalls,
              },
            };
          });
          setCharts(chartsMap);

          if (list.length >= 2) {
            setCompareCampAId(list[0].id);
            setCompareCampBId(list[1].id);
          } else if (list.length === 1) {
            setCompareCampAId(list[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to load campaigns list:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const campA = useMemo(
    () => campaigns.find((c) => c.id === compareCampAId) || campaigns[0],
    [campaigns, compareCampAId]
  );
  const campB = useMemo(
    () => campaigns.find((c) => c.id === compareCampBId) || campaigns[1] || campaigns[0],
    [campaigns, compareCampBId]
  );

  const statsA = useMemo(() => {
    if (!campA) return {};
    const conv = campA.clicks > 0 ? (campA.installs / campA.clicks) * 100 : 0;
    const roi = campA.spent > 0 ? ((campA.revenue - campA.spent) / campA.spent) * 100 : 0;
    const cpa = campA.installs > 0 ? Math.round(campA.spent / campA.installs) : 0;
    return { conv, roi, cpa };
  }, [campA]);

  const statsB = useMemo(() => {
    if (!campB) return {};
    const conv = campB.clicks > 0 ? (campB.installs / campB.clicks) * 100 : 0;
    const roi = campB.spent > 0 ? ((campB.revenue - campB.spent) / campB.spent) * 100 : 0;
    const cpa = campB.installs > 0 ? Math.round(campB.spent / campB.installs) : 0;
    return { conv, roi, cpa };
  }, [campB]);

  const chartOptionsCompare = useMemo(() => {
    const baseOptions = BaseOptionChart();
    const activeCampCharts = charts[campA?.id] || { daily: { dates: [] } };
    const dates = activeCampCharts.daily?.dates || activeCampCharts.dates || [];
    return {
      ...baseOptions,
      stroke: { width: [2.5, 2.5], curve: 'smooth' },
      labels: dates,
      xaxis: { type: 'datetime' },
      colors: [theme.palette.info.main, theme.palette.success.main],
      tooltip: {
        shared: true,
        y: {
          formatter: (y) => (typeof y !== 'undefined' ? `${y} installs` : y),
        },
      },
    };
  }, [campA, charts, theme]);

  const seriesCompare = useMemo(() => {
    if (!campA || !campB) return [];
    const getInstalls = (cId) => {
      const activeObj = charts[cId];
      if (!activeObj) return [];
      return activeObj.daily?.installs || activeObj.installs || [];
    };
    return [
      { name: `${campA.name} Installs`, data: getInstalls(campA.id) },
      { name: `${campB.name} Installs`, data: getInstalls(campB.id) },
    ];
  }, [campA, campB, charts]);

  if (campaigns.length < 2) {
    return (
      <Page title="Campaign Comparison">
        <Container maxWidth="xl">
          <Box mb={3}>
            <Button
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/dashboard/campaigns')}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Back to Campaigns
            </Button>
          </Box>
          <Alert severity="warning">
            Configure at least two campaigns to enable head-to-head performance comparisons.
          </Alert>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Head-to-Head Comparison">
      <Container maxWidth="xl">
        {/* Back Link */}
        <Box mb={3}>
          <Button
            startIcon={<ArrowLeft size={16} />}
            onClick={() => navigate('/dashboard/campaigns')}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: 'transparent',
              },
            }}
          >
            Back to Campaigns
          </Button>
        </Box>

        {/* Header Title */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={4}>
          <GitCompare size={28} color={theme.palette.primary.main} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
              Head-to-Head Campaign Comparison
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Select two ad channels to compare click referrals, install acquisition efficiency, and transaction margins
              side-by-side.
            </Typography>
          </Box>
        </Stack>

        {/* Apple style selector dropdowns */}
        <Card sx={{ p: 3, mb: 4, border: '1px solid #E5E5E7', boxShadow: 'none', borderRadius: '12px' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                select
                fullWidth
                label="Compare Campaign A"
                value={compareCampAId}
                onChange={(e) => setCompareCampAId(e.target.value)}
                variant="outlined"
              >
                {campaigns.map((c) => (
                  <MenuItem key={c.id} value={c.id} disabled={c.id === compareCampBId}>
                    {c.name} ({c.channel})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2} display="flex" justifyContent="center">
              <Typography variant="h6" color="text.secondary" fontWeight={700}>
                VS
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                select
                fullWidth
                label="Compare Campaign B"
                value={compareCampBId}
                onChange={(e) => setCompareCampBId(e.target.value)}
                variant="outlined"
              >
                {campaigns.map((c) => (
                  <MenuItem key={c.id} value={c.id} disabled={c.id === compareCampAId}>
                    {c.name} ({c.channel})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Card>

        {/* Spec Comparison Panel */}
        {campA && campB ? (
          <Grid container spacing={4}>
            {/* Left Block Spec-sheet comparison */}
            <Grid item xs={12} md={8}>
              <Card sx={{ border: '1px solid #E5E5E7', boxShadow: 'none', borderRadius: '12px' }}>
                <CardHeader
                  title="Side-by-Side Specifications"
                  subheader="Winner badges highlight campaigns with superior conversions and cost efficiency."
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                />
                <Divider />

                <Box p={3}>
                  <Stack spacing={4}>
                    {/* Header labels */}
                    <Grid container spacing={2} textAlign="center" pb={2} borderBottom="1px solid #F0F0F2">
                      <Grid item xs={5}>
                        <Typography variant="h5" fontWeight={700} color={theme.palette.primary.main}>
                          {campA.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campA.channel} • {campA.platform}
                        </Typography>
                      </Grid>
                      <Grid item xs={2} display="flex" alignItems="center" justifyContent="center">
                        <Typography variant="body2" color="text.disabled" fontWeight={600}>
                          VS
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="h5" fontWeight={700} color={theme.palette.primary.main}>
                          {campB.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campB.channel} • {campB.platform}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Metric: Conversion Rate */}
                    <CompareSpecRow
                      label="Install Conversion Ratio"
                      valueA={`${statsA.conv.toFixed(1)}%`}
                      valueB={`${statsB.conv.toFixed(1)}%`}
                      winner={statsA.conv > statsB.conv ? 'A' : 'B'}
                      tooltipText="Percentage of ad referrals successfully registering on target device."
                    />

                    {/* Metric: ROI */}
                    <CompareSpecRow
                      label="Estimated ROI"
                      valueA={`${statsA.roi.toFixed(0)}%`}
                      valueB={`${statsB.roi.toFixed(0)}%`}
                      winner={statsA.roi > statsB.roi ? 'A' : 'B'}
                      tooltipText="Net wallet funding revenue generated compared to cost spent."
                    />

                    {/* Metric: CPA */}
                    <CompareSpecRow
                      label="Cost Per Acquisition (CPA)"
                      valueA={`₦${fCurrency(statsA.cpa)}`}
                      valueB={`₦${fCurrency(statsB.cpa)}`}
                      winner={statsA.cpa < statsB.cpa ? 'A' : 'B'}
                      tooltipText="Average spent cost per app install device. Lower is better."
                    />

                    {/* Metric: Installs */}
                    <CompareSpecRow
                      label="Attributed Installs"
                      valueA={fNumber(campA.installs)}
                      valueB={fNumber(campB.installs)}
                      winner={campA.installs > campB.installs ? 'A' : 'B'}
                    />

                    {/* Metric: Wallet Revenue */}
                    <CompareSpecRow
                      label="Funded Wallet Revenue"
                      valueA={`₦${fCurrency(campA.revenue)}`}
                      valueB={`₦${fCurrency(campB.revenue)}`}
                      winner={campA.revenue > campB.revenue ? 'A' : 'B'}
                    />

                    {/* Metric: Budget spent */}
                    <CompareSpecRow
                      label="Allocated Budget"
                      valueA={`₦${fCurrency(campA.budget)}`}
                      valueB={`₦${fCurrency(campB.budget)}`}
                    />
                  </Stack>
                </Box>
              </Card>
            </Grid>

            {/* Right Block Timeline & Recommendations */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  border: '1px solid #E5E5E7',
                  boxShadow: 'none',
                  borderRadius: '12px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <CardHeader
                  title="Timeline Performance"
                  subheader="Installs generated by date"
                  titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                />
                <Divider />
                <CardContent>
                  <ReactApexChart type="line" series={seriesCompare} options={chartOptionsCompare} height={280} />
                </CardContent>

                <Box p={3} bgcolor="#F9FAFB">
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    gutterBottom
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                  >
                    Performance Insights
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {statsA.roi > statsB.roi
                      ? `🏆 **${
                          campA.name
                        }** is the more efficient funnel, realizing an Estimated ROI of **${statsA.roi.toFixed(
                          0
                        )}%** (vs ${statsB.roi.toFixed(0)}% for ${campB.name}).`
                      : `🏆 **${
                          campB.name
                        }** is the more efficient funnel, realizing an Estimated ROI of **${statsB.roi.toFixed(
                          0
                        )}%** (vs ${statsA.roi.toFixed(0)}% for ${campA.name}).`}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        ) : null}
      </Container>
    </Page>
  );
}

// ----------------------------------------------------------------------

function CompareSpecRow({ label, valueA, valueB, winner, tooltipText }) {
  const theme = useTheme();

  return (
    <Box borderBottom="1px solid #F0F0F2" pb={2.5}>
      <Stack direction="row" alignItems="center" justifyContent="center" mb={1}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {label}
          {tooltipText && (
            <Tooltip title={tooltipText} size="small">
              <Info size={13} style={{ cursor: 'pointer' }} />
            </Tooltip>
          )}
        </Typography>
      </Stack>
      <Grid container spacing={2} textAlign="center">
        <Grid item xs={5}>
          <Typography
            variant="h4"
            fontWeight={700}
            color="text.primary"
            sx={{
              transition: 'all 0.2s',
              py: 0.5,
              borderRadius: '8px',
              backgroundColor: winner === 'A' ? alpha(theme.palette.success.main, 0.08) : 'transparent',
              color: winner === 'A' ? theme.palette.success.darker : 'text.primary',
            }}
          >
            {valueA}
            {winner === 'A' && (
              <Chip
                label="Best"
                size="small"
                color="success"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  height: 16,
                  ml: 0.5,
                  backgroundColor: '#AAF27F',
                  color: '#08660D',
                }}
              />
            )}
          </Typography>
        </Grid>
        <Grid item xs={2} display="flex" alignItems="center" justifyContent="center" />
        <Grid item xs={5}>
          <Typography
            variant="h4"
            fontWeight={700}
            color="text.primary"
            sx={{
              transition: 'all 0.2s',
              py: 0.5,
              borderRadius: '8px',
              backgroundColor: winner === 'B' ? alpha(theme.palette.success.main, 0.08) : 'transparent',
              color: winner === 'B' ? theme.palette.success.darker : 'text.primary',
            }}
          >
            {winner === 'B' && (
              <Chip
                label="Best"
                size="small"
                color="success"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  height: 16,
                  mr: 0.5,
                  backgroundColor: '#AAF27F',
                  color: '#08660D',
                }}
              />
            )}
            {valueB}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
